import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
import { CartItem } from "../types/cart";
import { ProductDecision, ReplacementAction } from "modules/checkout/types/productDecission";

import Amount from "utils/amount";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"

import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet"

import {
    Card,
    CardContent,
} from "@/components/ui/card"

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel"

import {
    RadioGroup,
    RadioGroupItem,
} from "@/components/ui/radio-group"
import { Separator } from "@/components/ui/separator";

import { Label } from "@/components/ui/label"
import { ProductData } from "modules/product/types/product";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useState } from "react";
import { useFetch } from "modules/main/hooks/useFetch";
import { endPointProductAlternatives } from "modules/main/services/endpoints";
import { Button } from "@/components/ui/button";
import { Sparkle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface ReplacementPreferencesProps extends React.HTMLAttributes<HTMLDivElement>{
    product:CartItem,
    open: boolean,
    onOpenChange: (open: boolean) => void,
    onDecisionConfirmed?: (decision: ProductDecision) => void,
}
export const ReplacementPreferences = ({product, open, onOpenChange, onDecisionConfirmed}: ReplacementPreferencesProps) => {
    const isDesktop = useMediaQuery(isDesktopSize);
    const sku = product.price_component[0]?.sku ?? null;
    const alternativesUrl = sku ? endPointProductAlternatives(sku) : null;
    const { data: alternativesData, loading: alternativesLoading } = useFetch<ProductData[]>(alternativesUrl);
    const alternatives: ProductData[] = alternativesData ?? [];

    const handleConfirm = (decision: ProductDecision) => {
        onDecisionConfirmed?.(decision);
        onOpenChange(false);
    };
    return (
        (isDesktop)?(
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                <DialogTitle>Si no está disponible ...</DialogTitle>
                <DialogDescription>
                </DialogDescription>
                </DialogHeader>
                <ReplacementOptions 
                    className="mt-2"
                    product={product} 
                    alternatives={alternatives}
                    loading={alternativesLoading}
                    onDecisionChange={() => {}}
                    onConfirm={handleConfirm}/>
            </DialogContent>
        </Dialog>
        ):(
        <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side={"bottom"} className="min-h-[70dvh]">
            <SheetHeader className="gap-0">
                <SheetTitle>Si no está disponible ...</SheetTitle>
                <SheetDescription></SheetDescription>
            </SheetHeader>
            <ReplacementOptions 
                className="mt-2"
                product={product} 
                alternatives={alternatives}
                loading={alternativesLoading}
                onDecisionChange={() => {}}
                onConfirm={handleConfirm}/>
        </SheetContent>
        </Sheet>
        )
    );
}


interface ReplacementOptionsProps extends React.HTMLAttributes<HTMLDivElement>{
    product:CartItem,
    alternatives:ProductData[];
    loading?: boolean;
    onDecisionChange: (decision: ProductDecision) => void;
    onConfirm?: (decision: ProductDecision) => void;
}
const ReplacementOptions = ({product,alternatives,loading,...props}: ReplacementOptionsProps) => {
    const isDesktop = useMediaQuery(isDesktopSize);
    const imageUrl = product.price_component[0].images?.catalog??product.price_component[0].image_url;
    const productName = product.price_component[0].name;
    const qtyInCart = product.price_component.reduce((acc, pc) => acc + pc.quantity, 0);
    const brand = product.brand;
    const price = product.price_component.reduce((acc, pc) => {
        return acc + (pc.price);
    }, 0);
    const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
    const [action, setAction] = useState<ReplacementAction>("smart-replace");

    useEffect(() => {
        if (!loading && alternatives.length > 0) {
            setSelectedIds(alternatives.map(p => p.id));
        }
        if (!loading && alternatives.length === 0) {
            setAction("no-replacement");
        }
    }, [loading, alternatives]);

    useEffect(() => {
            if (props.onDecisionChange) {
                const selectedProducts = alternatives.filter(p => selectedIds.includes(p.id));
                props.onDecisionChange({
                    productId: product.id,
                    action,
                    replacementIds: selectedProducts.map((p) => p.price_detail.best_price_id)
                });
            }
    }, [action, selectedIds, alternatives, product.id, props.onDecisionChange]);

    const handleToggleAlternative = (id: number | string, checked: boolean) => {
        setSelectedIds(prev => {
            if (checked) {
                return [...prev, id];
            } else {
                return prev.filter(item => item !== id);
            }
        });
    };
    return (
    <div className="flex flex-col gap-2">
    <div className={cn("flex flex-row items-center justify-between gap-4",...props.className?[props.className]:[])}>
        <div className="flex flex-row gap-2 items-start text-sm">
            <img className="m-auto Image-50 flex-shrink-0 object-contain" src={imageUrl} alt={productName} />
            <div>
                <Dialog>
                    <DialogTitle></DialogTitle>
                    <DialogDescription></DialogDescription>
                    <DialogTrigger className="text-left">
                        <span className="font-semibold text-muted-foreground">{brand}</span> - <span className="text-sm font-semibold">{productName}</span>
                    </DialogTrigger>
                    <DialogContent className="min-w-[70%]">
                        {/* <ProductSheet productID={props.product.product.slug}/> */}
                    </DialogContent>
                </Dialog>                         
                {
                    <p className="text-sm font-medium text-muted-foreground">{qtyInCart} un - 
                        <Amount amount={price/qtyInCart} currency="CLP" locale="es-CL" className="ml-1"/> c/u
                    </p>
                }
            </div>                
        </div>
        <span className="text-base font-bold ml-auto">
            <Amount 
                className=""
                amount={price} 
                currency="CLP" 
                locale="es-CL" 
            />
        </span>
    </div>
    <Card className="py-2 mt-2">
        <CardContent className="p-2 text-sm">
            <RadioGroup value={action} onValueChange={(v) => setAction(v as ReplacementAction)}>
                {
            (loading || !(alternatives.length === 0)) && (<>
                <div className="flex items-center space-x-3 text-sm mx-2">
                    <RadioGroupItem value={"smart-replace"} id={`smart-replace-${product.id}`} disabled={loading}/>
                    <div className="flex flex-col gap-0">
                        <div className="flex items-center"><Label htmlFor={`smart-replace-${product.id}`}>Buscar la mejor alternativa</Label><Sparkle className="text-primary ml-2 w-4 h-5"/></div>
                        <span className="text-muted-foreground">Buscamos automáticamente la opción más similar disponible</span>
                    </div>
                </div>
                <Separator className=""/>
                <div className="flex items-center space-x-2 mx-2">
                    <RadioGroupItem value={"selected-replacement"} id={`selected-replacement-${product.id}`} disabled={loading}/>                                  
                    <div className="flex flex-col gap-1">
                        <Label htmlFor={`selected-replacement-${product.id}`} className="">Aceptar solo estos reemplazos</Label>
                        <span className="text-muted-foreground">Si no está disponible, usamos uno de estos</span>
                    </div>                                                            
                </div>
                    <div className={cn("min-h-56 max-w-full min-w-0 transition-all duration-200", {
                        "opacity-50 pointer-events-none grayscale": (action !== "selected-replacement" && action !== "selected-sustitution")
                    })}>
                        {loading ? (
                            <div className="flex gap-2 px-1 py-2 overflow-hidden">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="flex flex-col gap-2 w-24 shrink-0">
                                        <Skeleton className="h-20 w-full rounded-md" />
                                        <Skeleton className="h-3 w-12" />
                                        <Skeleton className="h-3 w-full" />
                                        <Skeleton className="h-3 w-3/4" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                        <Carousel
                            opts={{
                            align: "start",
                            }}
                            className={cn("w-full",
                                {"max-w-sm":!isDesktop}
                            )}
                            ><div className="flex flex-col">
                                <CarouselContent>
                                {alternatives.map((product, index) => {
                                    const image_url = product.stores[0].images?.catalog??product.stores[0].image_url;
                                    const isSelected = selectedIds.includes(product.id);
                                    return (
                                    <CarouselItem 
                                        key={`${product.id}-${product.id}`} 
                                        className="basis-1/3 md:basis-1/4 lg:basis-1/4"
                                    >
                                    <button
                                        type="button"
                                        onClick={() => handleToggleAlternative(product.id, !isSelected)}
                                        className={cn(
                                            "p-2 flex flex-col w-full text-left rounded-md border-2 transition-colors cursor-pointer",
                                            isSelected ? "border-primary bg-primary/5" : "border-transparent",
                                            !isDesktop && "max-w-28"
                                        )}
                                    >
                                        <div className="mx-auto py-2 relative w-full">
                                            <Checkbox 
                                                className="absolute top-0 right-0 z-10 bg-white pointer-events-none"
                                                checked={isSelected}
                                                tabIndex={-1}
                                            />
                                            <img className={cn("m-auto Image-80 flex-shrink-0 object-contain transition-opacity duration-200", {
                                                "opacity-40 grayscale": !isSelected
                                            })} src={image_url} alt={product.stores[0].name} />
                                        </div>
                                        <div>
                                            <Amount 
                                                className="text-sm font-bold"
                                                amount={product.price_detail.best_price} 
                                                currency="CLP" 
                                                locale="es-CL" 
                                            />
                                        </div>
                                        <div className="">
                                            <p className="text-muted-foreground line-clamp-2 text-xs">{product.brand}</p>
                                            <span className="text-muted-foreground line-clamp-2 text-xs">{product.stores[0].name}</span>
                                        </div>
                                    </button>
                                    </CarouselItem>
                                )}
                                )}
                                </CarouselContent>
                            <div className="flex gap-4">
                                <CarouselPrevious className="left-1 top-full mt-4"/>
                                <CarouselNext  className="right-1 top-full mt-4"/>
                            </div>
                        </div>
                        </Carousel>
                        )}
                    </div>                     
                <Separator className=""/>
            </>)
                }
                <div className="flex items-center space-x-2 mx-2">
                    <RadioGroupItem value="no-replacement" id={`no-replacement-${product.id}`} />
                    <div className="flex flex-col gap-1">
                        <Label htmlFor={`no-replacement-${product.id}`} className={cn("")}>{"No reemplazar"}</Label>
                        <span className="text-muted-foreground">
                            {"No incluir este producto"}
                        </span>
                    </div>
                </div>                    
            </RadioGroup>
            <div className="px-2 pt-3">
                <Button
                    className="w-full"
                    onClick={() => {
                        const selectedProducts = alternatives.filter(p => selectedIds.includes(p.id));
                        props.onConfirm?.({
                            productId: product.id,
                            action,
                            replacementIds: selectedProducts.map((p) => p.price_detail.best_price_id),
                        });
                    }}
                >
                    Confirmar
                </Button>
            </div>
        </CardContent>
    </Card>
</div>
    );
}

// interface ProductConfirmationItemProps{
//     // product:ProductDifference;
//     cart:CartItem[],
//     onDecisionChange: (decision: ProductDecision) => void;
// }

// const ProductConfirmationItem = forwardRef<HTMLDivElement, ProductConfirmationItemProps>((props,ref) => {
    
//     // const isNotAvailable = props.product.diff.includes("NOT_AVAILABLE");
//     const doesntHaveAlternatives = props.product.diff_alternatives.length === 0;
//     const qtyInCart = get_total_qty_per_prod(props.product.product.id,props.cart)

//     const [action, setAction] = useState<ReplacementAction>(
//         (isNotAvailable && doesntHaveAlternatives) ? "no-replacement" : (isNotAvailable)?"smart-sustitution":"smart-replace"
//     );
//     const [alternatives] = useState(props.product.diff_alternatives || []);
//     const [selectedIds, setSelectedIds] = useState<(number | string)[]>(
//         props.product.diff_alternatives?.map(p => p.id) || []
//     );

//     useEffect(() => {
//         if (props.onDecisionChange) {
//             const selectedProducts = alternatives.filter(p => selectedIds.includes(p.id));
//             props.onDecisionChange({
//                 productId: props.product.product.id,
//                 action,
//                 replacementIds: selectedProducts.map((p) => p.price_detail.best_price_id)
//             });
//         }
//     }, [action, selectedIds, alternatives, props.product.product.id, props.onDecisionChange]);

//     const handleToggleAlternative = (id: number | string, checked: boolean) => {
//         setSelectedIds(prev => {
//             if (checked) {
//                 return [...prev, id];
//             } else {
//                 return prev.filter(item => item !== id);
//             }
//         });
//     };
//     const imageUrl =props.product.product.stores[0].images?.catalog??props.product.product.stores[0].image_url;
//     return(
//     <Card className=" py-2">
//         <CardContent className="flex flex-col text-sm gap-4 px-2">

//     </Card>
//     );
// });